import { COUNT_PER_PAGE, YN } from '../../constants/Constants';
import { PC_BASE_BLUEPRINT } from '../../constants/PerformanceCheckConstants';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  columnCheckState,
  columnNumberWithCommas,
  columnTransferState,
} from '../../utils/dataTableUtil';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { useHistory } from 'react-router-dom';

import { Button } from 'primereact/button';
import CheckBaseDataDialog from '../../components/PerformanceCheck/CheckBaseDataDialog';
import { CodeUtil } from '../../utils/CodeUtil';
import { Column } from 'primereact/column';
import { CommonUtil } from '../../utils/commonUtil';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { RadioButton } from 'primereact/radiobutton';
import RangeCalendar from '../../components/Common/RangeCalendar';
import RecheckBaseDataDialog from '../../components/PerformanceCheck/RecheckBaseDataDialog';
import SearchMenu from '../../components/Menu/SearchMenu';
import { ServiceProvider } from '../../services';
import { Tag } from 'primereact/tag';
import { TieredMenu } from 'primereact/tieredmenu';
import TransferState from '../../enums/TransferState';
import UserRoleType from '../../enums/UserRoleType';
import PerformanceCheckKind, {
  getPerformanceCheckKindLabel,
  getPerformanceCheckKindTagClass,
  IS_EARLY_SCRAP_UI_ENABLED,
  isPseudoPerformanceCheck,
} from '../../enums/PerformanceCheckKind';
import _ from 'lodash';
import { classNames } from 'primereact/utils';
import dayjs from 'dayjs';
import { myInfoSelector } from '../../recoil/selectors';
import { touchUIState } from '../../recoil/atoms';
import { useAuthorizedFetch } from '../../hooks/useAuthorizedFetch';

const today = dayjs();

const DEFAULT_SEARCH_CONDITIONS = {
  associations: [],
  enterprises: [],
  shops: [],
  startDate: today.toDate(),
  endDate: today.toDate(),
  statementNumber: '',
  carSearchKeyword: '',
  customerSearchBy: '1',
  customerSearchKeyword: '',
  checkType: '',
  payYN: '',
  delYN: YN.NO,
};

const DEFAULT_LAZY_PARAMS = {
  first: 0,
  rows: 10,
  page: 0,
};

const DATE_PRESET_OPTIONS = [
  { value: 'today', label: '당일' },
  { value: 'thisMonth', label: '당월' },
  { value: 'lastMonth', label: '전월' },
  { value: 'oneYear', label: '1년' },
];

const getDateRangeByPreset = (preset) => {
  const now = dayjs();

  switch (preset) {
    case 'today':
      return { startDate: now.toDate(), endDate: now.toDate() };
    case 'thisMonth':
      return {
        startDate: now.startOf('month').toDate(),
        endDate: now.toDate(),
      };
    case 'lastMonth': {
      const prevMonth = now.subtract(1, 'month');
      return {
        startDate: prevMonth.startOf('month').toDate(),
        endDate: prevMonth.endOf('month').toDate(),
      };
    }
    case 'oneYear':
      return {
        startDate: now.subtract(1, 'year').toDate(),
        endDate: now.toDate(),
      };
    default:
      return null;
  }
};

const performanceCheckService = ServiceProvider.performanceCheck;
const performanceCheckPaymentService = ServiceProvider.performanceCheckPayment;

const priceFormatter = new Intl.NumberFormat('ko-KR', { style: 'decimal' });

const renderSummaryAmount = (label, amount) => (
  <span className="inline-flex align-items-center">
    <span className="font-bold text-red-600 mr-3">{label}</span>
    <span className="font-bold text-cyan-500">
      {priceFormatter.format(amount || 0)}
    </span>
    <span className="font-bold text-red-600">원</span>
  </span>
);

const getPerformanceInsuranceAmounts = ({
  isRecheck,
  insurancePrice = 0,
  insurancePriceVat = 0,
  insurancePriceRefundedAt,
  recheckDiffAmount,
}) => {
  const total =
    insurancePriceRefundedAt != null
      ? 0
      : (_.toNumber(insurancePrice) || 0) + (_.toNumber(insurancePriceVat) || 0);

  if (recheckDiffAmount != null) {
    return {
      performanceInsurance: isRecheck === YN.YES ? 0 : total,
      recheckDiff: isRecheck === YN.YES ? _.toNumber(recheckDiffAmount) || 0 : 0,
    };
  }

  if (isRecheck === YN.YES) {
    return { performanceInsurance: 0, recheckDiff: total };
  }

  return { performanceInsurance: total, recheckDiff: 0 };
};

const getCheckPriceTotal = ({
  checkPrice = 0,
  checkPriceVat = 0,
  discountedCheckPrice = 0,
}) =>
  (_.toNumber(checkPrice) || 0) +
  (_.toNumber(checkPriceVat) || 0) -
  (_.toNumber(discountedCheckPrice) || 0);

const isViewablePerformanceCheckReport = (row) =>
  isPseudoPerformanceCheck(row) ||
  [
    TransferState.Transfered,
    TransferState.Updated,
    TransferState.TransferReturn,
  ].includes(_.get(row, 'transferState'));

const openPerformanceCheckViewer = ({ statementNumber, dataId }) => {
  CommonUtil.Window.popup(
    `/performance-check/${statementNumber}-${dataId}/viewer`,
    `pcv#${statementNumber}`,
    window.innerWidth * 0.8,
    window.innerHeight * 0.8
  );
};

function formatPerformanceCheckListQueryParams(conditions) {
  const params = { ...conditions };
  for (const key in params) {
    if (key.toLowerCase().includes('date') && params[key]) {
      params[key] = dayjs(params[key]).format('YYYYMMDD');
    }
  }
  return params;
}

const PerformanceCheckList = () => {
  const history = useHistory();
  const touchUI = useRecoilValue(touchUIState);
  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);

  const myInfo = useMemo(
    () => MyInfoUtil.unpack(myInfoLoadable.contents),
    [myInfoLoadable.contents]
  );

  const isShopAccount = useMemo(
    () => _.startsWith(myInfo.myRoleCode, 'S_'),
    [myInfo.myRoleCode]
  );

  const defaultSearchConditions = useMemo(() => {
    const { myAssociationId, myEnterpriseId, myShopId, myRoleCode } = myInfo;
    const deleteAuth = ['CERP_ADM', 'A_MST', 'A_ADM'].includes(myRoleCode);

    return {
      ...DEFAULT_SEARCH_CONDITIONS,
      delYN: isShopAccount || !deleteAuth ? YN.NO : '',
      associations: myAssociationId ? [myAssociationId] : [],
      enterprises: myEnterpriseId ? [myEnterpriseId] : [],
      shops: myShopId ? [myShopId] : [],
    };
  }, [myInfo, isShopAccount]);

  const [searchConditions, setSearchConditions] = useState({
    ...defaultSearchConditions,
  });
  const [datePreset, setDatePreset] = useState('today');
  const [lazyParams, setLazyParams] = useState({ ...DEFAULT_LAZY_PARAMS });

  const menu = useRef(null);
  const { loading, totalRecords, data, getList, initList } = useAuthorizedFetch(
    performanceCheckService,
    searchConditions,
    setSearchConditions,
    defaultSearchConditions,
    lazyParams.page,
    lazyParams.rows
  );

  const [currentStatementNumber, setCurrentStatementNumber] = useState(null);
  const [currentDataId, setCurrentDataId] = useState(null);
  const [originStatementNumber, setOriginStatementNumber] = useState(null);
  const [codes, setCodes] = useState({});
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [menuItem, setMenuItem] = useState(null);
  /** PrimeReact dataKey 기준 펼침 행 (lazy 페이징과 분리) */
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedGroupRows, setExpandedGroupRows] = useState({});
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(
    () => new Set()
  );

  useEffect(() => {
    if (myInfoLoadable.state !== 'hasError') {
      return;
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('isLoggedIn');
    history.replace('/login');
  }, [myInfoLoadable.state, history]);

  const collapseAllGroupRows = useCallback(() => {
    setExpandedRows({});
  }, []);

  const toggleRowExpand = useCallback(
    async (rowData, event) => {
      event?.stopPropagation?.();
      const rowKey = String(rowData.dataId);
      const statementNumber = rowData.statementNumber;

      if (expandedRows[rowKey]) {
        const next = { ...expandedRows };
        delete next[rowKey];
        setExpandedRows(next);
        return;
      }

      if (!expandedGroupRows[statementNumber]) {
        setLoadingGroupMembers((prev) => new Set(prev).add(statementNumber));
        try {
          const params = formatPerformanceCheckListQueryParams(searchConditions);
          const {
            data: { list },
          } = await performanceCheckService.listGroupMembers(statementNumber, {
            ...params,
            excludeDataId: rowData.dataId,
          });
          setExpandedGroupRows((prev) => ({
            ...prev,
            [statementNumber]: list || [],
          }));
        } catch (error) {
          window.cerp.dialog.error(
            '이력 조회 실패',
            `[${error?.code}] ${error?.message}`
          );
          return;
        } finally {
          setLoadingGroupMembers((prev) => {
            const next = new Set(prev);
            next.delete(statementNumber);
            return next;
          });
        }
      }

      setExpandedRows((prev) => ({ ...prev, [rowKey]: true }));
    },
    [expandedRows, expandedGroupRows, searchConditions]
  );

  const getSummary = async (conditions) => {
    const params = { ...conditions };
    delete params.delYN;

    for (let key in params) {
      if (key.toLowerCase().includes('date')) {
        params[key] = dayjs(params[key]).format('YYYYMMDD');
      }
    }

    try {
      const { data } = await performanceCheckPaymentService.summary({
        ...params,
        page: lazyParams.page + 1,
        size: lazyParams.rows,
      });
      setSummary(data || {});
    } catch (error) {
      window.cerp.dialog.error(
        '합계 조회 실패',
        `[${error?.code}] ${error?.message}`
      );
    }
  };

  async function exportToExcel() {
    setExporting(true);

    const params = {
      ...searchConditions,
      page: lazyParams.page + 1,
      size: lazyParams.rows,
    };
    params.startDate = dayjs(searchConditions.startDate).format('YYYYMMDD');
    params.endDate = dayjs(searchConditions.endDate).format('YYYYMMDD');

    await performanceCheckService.exportExcel(params);

    setExporting(false);
  }

  const handleDatePresetChange = (preset) => {
    const range = getDateRangeByPreset(preset);
    if (!range) return;

    setDatePreset(preset);
    setSearchConditions((ps) => ({
      ...ps,
      startDate: range.startDate,
      endDate: range.endDate,
    }));
  };

  function checkAuth(userInfo, type) {
    const { roleCode } = userInfo;

    const ACCESS_PERMISSION = {
      regist: _.filter(UserRoleType, function (r) {
        return (
          _.startsWith(r.value, 'P') ||
          _.startsWith(r.value, 'S') ||
          r.value === 'A_MST' ||
          r.value === 'CERP_ADM' ||
          r.value === 'E_OFF'
        );
      }),
      update: _.filter(UserRoleType, function (r) {
        return (
          _.startsWith(r.value, 'C_') ||
          r.value === 'A_OFF' ||
          r.value === 'E_ADM'
        );
      }),
      getDeleteList: _.filter(UserRoleType, function (r) {
        return (
          r.value === 'CERP_ADM' || r.value === 'A_MST' || r.value === 'A_ADM'
        );
      }),
      getMonthlyList: _.filter(UserRoleType, function (r) {
        return r.value === 'CERP_ADM' || _.startsWith(r.value, 'A_');
      }),
    };

    return _.findIndex(ACCESS_PERMISSION[type], { value: roleCode }) >= 0;
  }

  function handleSelectAll(
    selected,
    field,
    updateCodes,
    updateSearchConditions
  ) {
    const update = [];

    if (selected && selected.includes('')) {
      update.push(
        ..._.get(codes, `${field}`).map((option) => {
          if (option.value !== '') {
            option.disabled = true;
          }
          return option;
        })
      );

      setCodes((ps) => ({ ...ps, [field]: update, ...updateCodes }));
      setSearchConditions((ps) => ({
        ...ps,
        [field]: [_.get(codes[field], '0.value')],
        ...updateSearchConditions,
      }));
      return;
    }

    update.push(
      ..._.get(codes, `${field}`).map((option) => {
        if (option.disabled) delete option.disabled;
        return option;
      })
    );

    setCodes((ps) => ({ ...ps, [field]: update, ...updateCodes }));

    if (selected === null) {
      setSearchConditions((ps) => ({ ...ps, [field]: '' }));
      return;
    }

    setSearchConditions((ps) => ({
      ...ps,
      [field]: selected,
      ...updateSearchConditions,
    }));
  }

  useEffect(() => {
    setSearchConditions((ps) => ({ ...ps, ...defaultSearchConditions }));
    window.getListByChildren = async function () {
      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
      await getList(
        defaultSearchConditions,
        _.get(DEFAULT_LAZY_PARAMS, 'page'),
        _.get(DEFAULT_LAZY_PARAMS, 'rows')
      );
      await getSummary(defaultSearchConditions);
    };

    return () => {
      delete window.getListByChildren;
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchConditions]);

  useEffect(() => {
    (async () => {
      if (myInfoLoadable.state === 'hasValue' && !isShopAccount) {
        const { myAssociationId, myEnterpriseId } = myInfo;

        const [associations] = await CodeUtil.getAssociationCodes(myInfo, {
          preSelectedAssociationId: myAssociationId,
        });
        const [enterprises] = await CodeUtil.getEnterpriseCodes(myInfo, {
          isParentEmpty: _.isEmpty(associations),
          associationId: myAssociationId,
        });
        const [shops] = await CodeUtil.getShopCodes(myInfo, {
          isParentEmpty: _.isEmpty(enterprises),
          enterpriseId: myEnterpriseId,
        });
        setCodes((ps) => ({ ...ps, associations, enterprises, shops }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfoLoadable.contents, isShopAccount]);

  useEffect(() => {
    if (myInfoLoadable.state !== 'hasValue') return;
    getList(
      defaultSearchConditions,
      DEFAULT_LAZY_PARAMS.page,
      DEFAULT_LAZY_PARAMS.rows
    );
    getSummary(defaultSearchConditions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfoLoadable.state, defaultSearchConditions]);

  const searchInList = () => {
    collapseAllGroupRows();
    setExpandedGroupRows({});
    getList(
      searchConditions,
      _.get(DEFAULT_LAZY_PARAMS, 'page'),
      _.get(DEFAULT_LAZY_PARAMS, 'rows')
    );
    getSummary(searchConditions);
    setSelectedRows([]);
    setLazyParams(DEFAULT_LAZY_PARAMS);
  };

  const renderPerformanceCheckListColumns = (variant = 'main') => {
    const isNested = variant === 'nested';

    return [
      !isNested ? (
        <Column
          key="group-toggle"
          header=""
          className="text-center"
          style={{ width: '3rem' }}
          body={(rowData) => {
            const groupCount = Number(rowData.groupCount) || 1;
            if (groupCount <= 1) {
              return null;
            }
            const rowKey = String(rowData.dataId);
            const expanded = !!expandedRows[rowKey];
            const isLoading = loadingGroupMembers.has(rowData.statementNumber);
            return (
              <Button
                type="button"
                icon={
                  isLoading
                    ? 'pi pi-spin pi-spinner'
                    : expanded
                      ? 'pi pi-minus'
                      : 'pi pi-plus'
                }
                className="p-button-text p-button-sm pcl-group-toggle"
                disabled={isLoading}
                title={
                  expanded
                    ? `동일 성능번호 ${groupCount}건 접기`
                    : `동일 성능번호 ${groupCount - 1}건 더 보기`
                }
                aria-label={expanded ? '이전 이력 접기' : '이전 이력 펼치기'}
                onClick={(e) => toggleRowExpand(rowData, e)}
              />
            );
          }}
        />
      ) : null,
      <Column
        key="row-index"
        field="dtn"
        header="순번"
        className="text-center"
        body={(rowData, column) =>
          isNested ? (
            <span className="text-color-secondary text-sm">└</span>
          ) : (
            <span>{column.rowIndex + 1}</span>
          )
        }
      />,
      <Column
        key="license-plate"
        field="licensePlateNumber"
        header={
          <div className="w-10rem">
            차량번호
            <br />
            차량모델
          </div>
        }
        body={({ licensePlateNumber, carName }) => (
          <div className="w-10rem">
            {licensePlateNumber}
            <br />
            <p
              className="w-10rem"
              style={{
                wordBreak: 'break-all',
                whiteSpace: 'normal',
              }}
            >
              {carName}
            </p>
          </div>
        )}
      />,
      <Column
        key="customer"
        field="customerCompanyName"
        header={
          <div>
            소속상사
            <br />
            고객명
          </div>
        }
        body={({ customerCompanyName, customerName }) => (
          <div>
            {customerCompanyName}
            <br />
            {customerName}
          </div>
        )}
      />,
      <Column
        key="statement-number"
        field="statementNumber"
        header="성능지 번호"
        body={(rowData) => {
          const { dataId, statementNumber } = rowData;
          const groupCount = Number(rowData.groupCount) || 1;
          const groupBadge =
            !isNested && groupCount > 1 && !expandedRows[String(dataId)] ? (
              <span className="pcl-group-count-badge">+{groupCount - 1}</span>
            ) : null;
          return (
            <div className="flex align-items-center justify-content-center gap-1">
              {checkAuth(myInfoLoadable.contents, 'update') ? (
                <span>{statementNumber}</span>
              ) : (
                <span
                  className="text-primary cursor-pointer"
                  onClick={() => {
                    setCurrentStatementNumber(statementNumber);
                    setCurrentDataId(dataId);
                  }}
                >
                  {statementNumber}
                </span>
              )}
              {groupBadge}
            </div>
          );
        }}
      />,
      <Column
        key="print-menu"
        field="statementNumber"
        header={<i className="pi pi-bars" />}
        body={(rowData) => (
          <Button
            icon="pi pi-bars"
            className="p-button-sm p-button-outlined"
            onClick={(event) => {
              setMenuItem(rowData);
              menu.current.toggle(event);
            }}
            aria-haspopup
            aria-controls="overlay_tmenu"
          />
        )}
      />,
      <Column
        key="check-kind"
        field="checkKind"
        header="점검종류"
        className="text-center"
        body={(rowData) => (
          <Tag
            className={classNames(
              'text-sm',
              getPerformanceCheckKindTagClass(rowData)
            )}
            value={getPerformanceCheckKindLabel(rowData)}
          />
        )}
      />,
      <Column
        key="check-date"
        field="checkDate"
        header="점검일자"
        body={({ checkDate }) => `${dayjs(checkDate).format('YYYY-MM-DD')}`}
      />,
      <Column
        key="car-year"
        field="carYear"
        header={
          <div>
            차량연식
            <br />
            주행거리
          </div>
        }
        className="text-right"
        body={({ carYear, carDistance }) => (
          <div>
            {carYear} 년식
            <br />
            {columnNumberWithCommas(carDistance)} Km
          </div>
        )}
      />,
      <Column
        key="car-vin"
        field="carVIN"
        header="차대번호"
        body={({ carVIN }) => <div>{carVIN}</div>}
      />,
      <Column
        key="check-state"
        field="checkState"
        className="text-center"
        header="점검상태"
        body={({ checkState }) => columnCheckState(checkState)}
      />,
      <Column
        key="check-date-diff"
        field="checkDateDiff"
        header="경과일수"
        className="text-center"
        body={({ checkDateDiff }) => `${checkDateDiff}일`}
      />,
      <Column
        key="transfer-state"
        field="transferState"
        header="전송상태"
        className="text-center"
        body={({ transferState }) => columnTransferState(transferState)}
      />,
      <Column
        key="transfer-time"
        field="initialTransferTime"
        header={
          <div>
            최초전송일시
            <br />
            최근전송일시
          </div>
        }
        body={({ initialTransferTime, latestTransferTime }) => (
          <div>
            {initialTransferTime !== null
              ? dayjs(initialTransferTime).format('YYYY-MM-DD HH:mm:ss')
              : ''}
            <br />
            {latestTransferTime !== null
              ? dayjs(latestTransferTime).format('YYYY-MM-DD HH:mm:ss')
              : ''}
          </div>
        )}
      />,
      <Column
        key="assurance"
        field="assuranceType"
        header={
          <div>
            보증유형
            <br />
            보험사
          </div>
        }
        body={({ assuranceType, insuranceCode }) => (
          <div>
            {_.get(
              _.find(PC_BASE_BLUEPRINT.ASSRNC_TY_SE_CODE.valueItems, {
                value: assuranceType,
              }),
              'label'
            )}
            <br />
            {_.get(
              _.find(codes.insurances, {
                value: insuranceCode,
              }),
              'label'
            )}
          </div>
        )}
      />,
      !isShopAccount ? (
        <Column
          key="shop-name"
          field="shopName"
          header="점검장명"
          className="text-center"
          body={({ shopName }) => `${shopName}`}
        />
      ) : null,
      <Column
        key="check-price"
        field="checkPrice"
        header="성능점검료"
        className="text-right"
        body={(rowData) =>
          priceFormatter.format(getCheckPriceTotal(rowData))
        }
      />,
      <Column
        key="insurance-price"
        field="insurancePrice"
        header={
          <div>
            성능보험료
            <br />
            재점검차액
          </div>
        }
        className="text-right"
        body={(rowData) => {
          const { performanceInsurance, recheckDiff } =
            getPerformanceInsuranceAmounts(rowData);

          return (
            <div>
              <div>{priceFormatter.format(performanceInsurance)}</div>
              <div>{priceFormatter.format(recheckDiff)}</div>
            </div>
          );
        }}
      />,
    ];
  };

  return (
    <div className="grid pcl">
      <div className="col-12">
        {(() => {
          switch (myInfoLoadable.state) {
            case 'hasValue': {
              const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);
              const showDeleteListFilter =
                !isShopAccount &&
                checkAuth(myInfoLoadable.contents, 'getDeleteList');

              return (
                <>
                  <SearchMenu
                    onInit={() => {
                      setLazyParams(DEFAULT_LAZY_PARAMS);
                      setDatePreset('today');
                      collapseAllGroupRows();
                      setExpandedGroupRows({});
                      initList();
                      getSummary(defaultSearchConditions);
                      setSelectedRows([]);
                    }}
                    onSearch={() => {
                      searchInList();
                    }}
                  >
                    <div
                      className={classNames('field col-12', {
                        'lg:col-4': showDeleteListFilter,
                        'lg:col-8': !showDeleteListFilter,
                      })}
                    >
                      <label htmlFor="range">성능점검일자</label>
                      <div className="flex align-items-center flex-wrap gap-2">
                        <RangeCalendar
                          startDate={searchConditions.startDate}
                          endDate={searchConditions.endDate}
                          onStartDateChanged={(date) => {
                            setDatePreset(null);
                            setSearchConditions((ps) => ({
                              ...ps,
                              startDate: date,
                            }));
                          }}
                          onEndDateChanged={(date) => {
                            setDatePreset(null);
                            setSearchConditions((ps) => ({
                              ...ps,
                              endDate: date,
                            }));
                          }}
                          touchUI={touchUI}
                          showNavigators={true}
                        />
                        <div className="p-buttonset flex-shrink-0">
                          {DATE_PRESET_OPTIONS.map((option) => (
                            <Button
                              key={option.value}
                              label={option.label}
                              className={classNames('p-button-sm', {
                                'p-button-outlined': datePreset !== option.value,
                              })}
                              onClick={() =>
                                handleDatePresetChange(option.value)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="field col-12 lg:col-4">
                      <label htmlFor="checkType">점검종류</label>
                      <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_all"
                            name="checkType"
                            value=""
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === ''}
                          />
                          <label htmlFor="radio_checkType_all">전체</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_new"
                            name="checkType"
                            value="N"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === 'N'}
                          />
                          <label htmlFor="radio_checkType_new">신규성능</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_re"
                            name="checkType"
                            value="Y"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === 'Y'}
                          />
                          <label htmlFor="radio_checkType_re">재성능</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_pseudo"
                            name="checkType"
                            value={PerformanceCheckKind.PSEUDO}
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={
                              searchConditions.checkType ===
                              PerformanceCheckKind.PSEUDO
                            }
                          />
                          <label htmlFor="radio_checkType_pseudo">가성능</label>
                        </div>
                        {IS_EARLY_SCRAP_UI_ENABLED && (
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_checkType_early_scrap"
                              name="checkType"
                              value={PerformanceCheckKind.EARLY_SCRAP}
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  checkType: e.value,
                                }));
                              }}
                              checked={
                                searchConditions.checkType ===
                                PerformanceCheckKind.EARLY_SCRAP
                              }
                            />
                            <label htmlFor="radio_checkType_early_scrap">
                              조기폐차
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {showDeleteListFilter && (
                      <div className="field col-12 lg:col-4">
                        <label htmlFor="delYN">삭제여부</label>
                        <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_a"
                              name="delYN"
                              value=""
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                              }}
                              checked={searchConditions.delYN === ''}
                            />
                            <label htmlFor="radio_delYn_a">전체</label>
                          </div>
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_y"
                              name="delYN"
                              value="Y"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                              }}
                              checked={searchConditions.delYN === 'Y'}
                            />
                            <label htmlFor="radio_delYn_y">삭제</label>
                          </div>
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_n"
                              name="delYN"
                              value="N"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                              }}
                              checked={searchConditions.delYN === 'N'}
                            />
                            <label htmlFor="radio_delYn_n">미삭제</label>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isShopAccount && (
                      <>
                        <div className="field col-12 sm:col-6 lg:col-4">
                          <label htmlFor="associations">소속협회</label>
                          <MultiSelect
                            className="w-full"
                            options={codes.associations}
                            display="chip"
                            placeholder="전체 협회"
                            value={searchConditions.associations}
                            filter
                            showSelectAll={false}
                            onChange={async ({ value }) => {
                              const [enterprises] =
                                await CodeUtil.getEnterpriseCodes(myInfo, {
                                  isParentEmpty: _.isEmpty(
                                    _.get(codes, 'associations')
                                  ),
                                  associationId: value,
                                });
                              const [shops] = await CodeUtil.getShopCodes(
                                myInfo,
                                {
                                  isParentEmpty: _.isEmpty(enterprises),
                                }
                              );

                              handleSelectAll(
                                value,
                                'associations',
                                {
                                  enterprises,
                                  shops,
                                },
                                {
                                  enterprises: [],
                                  shops: [],
                                }
                              );
                            }}
                          />
                        </div>
                        <div className="field col-12 sm:col-6 lg:col-4">
                          <label htmlFor="enterprises">소속점검법인</label>
                          <MultiSelect
                            className="w-full"
                            options={codes.enterprises}
                            display="chip"
                            placeholder="전체 업체"
                            value={searchConditions.enterprises}
                            filter
                            showSelectAll={false}
                            onChange={async ({ value }) => {
                              const [shops] = await CodeUtil.getShopCodes(
                                myInfo,
                                {
                                  isParentEmpty: _.isEmpty(
                                    _.get(codes, 'enterprises')
                                  ),
                                  enterpriseId: value,
                                }
                              );

                              handleSelectAll(
                                value,
                                'enterprises',
                                {
                                  shops,
                                },
                                {
                                  shops: [],
                                }
                              );
                            }}
                          />
                        </div>
                        <div className="field col-12 sm:col-6 lg:col-4">
                          <label htmlFor="shops">소속점검장</label>
                          <MultiSelect
                            className="w-full"
                            options={codes.shops}
                            display="chip"
                            placeholder="전체 점검장"
                            value={searchConditions.shops}
                            filter
                            showSelectAll={false}
                            onChange={({ value }) => {
                              handleSelectAll(value, 'shops');
                            }}
                          />
                        </div>
                      </>
                    )}

                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label htmlFor="customerSearchKeyword">고객</label>
                      <InputText
                        className="w-full"
                        value={searchConditions.customerSearchKeyword}
                        placeholder={
                          searchConditions.customerSearchBy === '1'
                            ? '고객 이름으로 검색'
                            : '매매상사 이름, 대표전화, 사업자등록번호로 검색'
                        }
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            customerSearchKeyword: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter' ? searchInList() : null
                        }
                      />
                      <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_dealerName"
                            name="customerSearchBy"
                            value="1"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                customerSearchBy: e.value,
                              }));
                            }}
                            checked={searchConditions.customerSearchBy === '1'}
                          />
                          <label htmlFor="radio_dealerName">고객</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_companyName"
                            name="customerSearchBy"
                            value="2"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                customerSearchBy: e.value,
                              }));
                            }}
                            checked={searchConditions.customerSearchBy === '2'}
                          />
                          <label htmlFor="radio_companyName">매매상사</label>
                        </div>
                      </div>
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label htmlFor="range">차량</label>
                      <InputText
                        className="w-full"
                        value={searchConditions.carSearchKeyword}
                        placeholder="차량번호, 차대번호로 검색"
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            carSearchKeyword: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter' ? searchInList() : null
                        }
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label htmlFor="statementNumber">성능지번호</label>
                      <InputText
                        id="statementNumber"
                        className="w-full"
                        value={searchConditions.statementNumber}
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            statementNumber: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter' ? searchInList() : null
                        }
                      />
                    </div>
                  </SearchMenu>

                  <div className="card flex flex-auto flex-wrap align-items-center justify-content-between gap-2">
                    <div className="flex flex-wrap align-items-center">
                      <i className="pi pi-tag mr-2 text-600 text-lg" />
                      {renderSummaryAmount(
                        '총 합계',
                        _.get(summary, 'sumTotalPrice')
                      )}
                      <span className="font-bold text-red-600 mx-2">/</span>
                      {renderSummaryAmount(
                        '총 점검료',
                        _.get(summary, 'sumTotalCheckPrice')
                      )}
                      <span className="font-bold text-red-600 mx-2">/</span>
                      {renderSummaryAmount(
                        '총 보험료',
                        _.get(summary, 'sumTotalInsurancePrice')
                      )}
                    </div>
                    <div className="flex gap-1">
                      {checkAuth(myInfoLoadable.contents, 'regist') && (
                        <>
                          {IS_EARLY_SCRAP_UI_ENABLED && (
                            <Button
                              label="조기폐차"
                              icon="pi pi-file"
                              className="p-button-outlined p-button-secondary"
                              onClick={() => {
                                CommonUtil.Window.popup(
                                  '/performance-check/early-scrap/form'
                                );
                              }}
                            />
                          )}
                          <Button
                            label="가성능"
                            icon="pi pi-file-edit"
                            className="p-button-outlined p-button-help"
                            onClick={() => {
                              CommonUtil.Window.popup(
                                '/performance-check/form?checkKind=P'
                              );
                            }}
                          />
                          <Button
                            label="성능점검 신규 등록"
                            icon="pi pi-check-square"
                            onClick={() => {
                              CommonUtil.Window.popup(
                                '/performance-check/form'
                              );
                            }}
                          />
                        </>
                      )}

                      <Button
                        label="엑셀 다운로드"
                        className="p-button-outlined"
                        icon="pi pi-download"
                        disabled={exporting || !data.length}
                        loading={exporting}
                        onClick={async () => await exportToExcel()}
                      />
                    </div>
                  </div>

                  <Panel className="shadow-1 mb-3">
                    <TieredMenu
                      ref={menu}
                      id="overlay_tmenu"
                      popup
                      model={
                        checkAuth(myInfoLoadable.contents, 'update')
                          ? [
                              {
                                label: '성능지 보기',
                                icon: 'pi pi-fw pi-file',
                                disabled: !isViewablePerformanceCheckReport(menuItem),
                                command: (event) => {
                                  openPerformanceCheckViewer(menuItem);
                                  menu.current.hide(event);
                                },
                              },
                            ]
                          : [
                              {
                                label: '성능지 보기',
                                icon: 'pi pi-fw pi-file',
                                disabled: !isViewablePerformanceCheckReport(menuItem),
                                command: (event) => {
                                  openPerformanceCheckViewer(menuItem);
                                  menu.current.hide(event);
                                },
                              },
                              {
                                label: '재성능 등록',
                                icon: 'pi pi-fw pi-clone',
                                disabled: !(
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.Transfered ||
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.Updated ||
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.TransferReturn
                                ),

                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  setCurrentDataId(dataId);
                                  setOriginStatementNumber(statementNumber);
                                  menu.current.hide(event);
                                },
                              },
                            ]
                      }
                      onHide={() => setMenuItem(null)}
                    />
                    <DataTable
                      loading={loading}
                      value={data}
                      dataKey="dataId"
                      lazy
                      rows={lazyParams.rows}
                      first={lazyParams.first}
                      totalRecords={totalRecords}
                      paginator
                      responsiveLayout="scroll"
                      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      currentPageReportTemplate="전체 {totalRecords}건 중 {first} ~ {last}"
                      rowsPerPageOptions={COUNT_PER_PAGE}
                      resizableColumns
                      showGridlines
                      expandedRows={expandedRows}
                      onRowToggle={(e) => setExpandedRows(e.data)}
                      rowExpansionTemplate={(rowData) => {
                        const statementNumber = rowData.statementNumber;
                        const members = expandedGroupRows[statementNumber];
                        const isLoading =
                          loadingGroupMembers.has(statementNumber);

                        if (isLoading) {
                          return (
                            <div className="pcl-group-expand-panel">
                              <ProgressBar
                                mode="indeterminate"
                                style={{ height: '4px' }}
                              />
                            </div>
                          );
                        }

                        if (!members?.length) {
                          return (
                            <div className="pcl-group-expand-panel p-3 text-center text-color-secondary">
                              표시할 이전 이력이 없습니다.
                            </div>
                          );
                        }

                        return (
                          <div className="pcl-group-expand-panel">
                            <DataTable
                              value={members}
                              dataKey="dataId"
                              showGridlines
                              responsiveLayout="scroll"
                              className="pcl-group-members-datatable pcl-group-members-datatable--no-header"
                            >
                              {renderPerformanceCheckListColumns('nested')}
                            </DataTable>
                          </div>
                        );
                      }}
                      onPage={async (event) => {
                        collapseAllGroupRows();
                        setLazyParams(event);
                        await getList(searchConditions, event.page, event.rows);
                      }}
                      selection={selectedRows}
                      selectionMode="checkbox"
                      onSelectionChange={({ value }) => {
                        setSelectedRows(value);
                      }}
                      emptyMessage="데이터가 없습니다."
                    >
                      {renderPerformanceCheckListColumns('main')}
                    </DataTable>
                  </Panel>

                  {currentStatementNumber !== null && (
                    <CheckBaseDataDialog
                      visible
                      userInfo={myInfoLoadable.contents}
                      dataId={currentDataId}
                      statementNumber={currentStatementNumber}
                      onHide={() => {
                        setCurrentStatementNumber(null);
                        setCurrentDataId(null);
                        setSelectedRows([]);
                      }}
                      getList={getList}
                      searchConditions={searchConditions}
                      page={lazyParams.page}
                      rows={lazyParams.rows}
                    />
                  )}

                  {originStatementNumber !== null && (
                    <RecheckBaseDataDialog
                      visible
                      userInfo={myInfoLoadable.contents}
                      dataId={currentDataId}
                      originStatementNumber={originStatementNumber}
                      onHide={async () => {
                        setOriginStatementNumber(null);
                        setCurrentDataId(null);
                        await getList(
                          searchConditions,
                          lazyParams.page,
                          lazyParams.rows
                        );
                        await getSummary(searchConditions);
                        setSelectedRows([]);
                      }}
                    />
                  )}
                </>
              );
            }
            case 'loading':
              return <ProgressBar mode="indeterminate" />;

            case 'hasError':
              return (
                <div className="p-4 text-center text-red-500">
                  사용자 정보를 불러오지 못했습니다. 로그인 화면으로 이동합니다.
                </div>
              );

            default:
              return <ProgressBar mode="indeterminate" />;
          }
        })()}
      </div>
    </div>
  );
};

const comparisonFn = function (prevProps, nextProps) {
  return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(PerformanceCheckList, comparisonFn);
